import React, { useEffect } from "react";
import { Mail, ShieldAlert, CheckCircle2, Trash2, ArrowRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function DataDeletionPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useDocumentMeta({
    title: "Data Deletion Instructions | TeleMedCare - Delete Account & Data",
    description: "Find instructions on how to delete your TeleMedCare account and erase your personal data. Request deletion via email hello@telemedcare.app.",
    canonical: "https://telemedcare.app/delete-account",
  });

  const sections = [
    { id: "overview", title: "1. Deletion Overview" },
    { id: "how-to", title: "2. How to Request Deletion" },
    { id: "what-deleted", title: "3. What Data Is Deleted" },
    { id: "what-retained", title: "4. What Data Is Retained" },
    { id: "timeframe", title: "5. Timeframe & Completion" },
  ];

  const emailSubject = encodeURIComponent("TeleMedCare Account & Data Deletion Request");
  const emailBody = encodeURIComponent(
    "Hello TeleMedCare Support,\n\nI would like to request the deletion of my TeleMedCare account and all associated personal and medical data.\n\nRegistered Phone Number:\nRegistered Name:\n\nThank you."
  );
  const mailtoUrl = `mailto:hello@telemedcare.app?subject=${emailSubject}&body=${emailBody}`;

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Navbar />

      <main className="pt-[72px]">
        {/* Header Hero Section */}
        <section className="bg-ink text-cream py-16 lg:py-24 relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-forest/20 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-32 left-0 h-[300px] w-[300px] rounded-full bg-clay/20 blur-3xl"
          />
          <div className="container-x relative">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-clay/15 border border-clay/30 px-3.5 py-1.5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
                <Trash2 className="h-3.5 w-3.5" />
                Data Deletion
              </span>
              <h1 className="mt-5 font-display text-[40px] sm:text-[52px] font-extrabold leading-[1.1] tracking-tight text-balance">
                Data Deletion Instructions
              </h1>
              <p className="mt-4 text-[16px] sm:text-[18px] text-cream/80 leading-relaxed">
                Step-by-step guidance on how to permanently delete your account, clinical logs, and personal information from TeleMedCare.
              </p>
            </div>
          </div>
        </section>

        {/* Core Layout - Sidebar + Content */}
        <section className="py-16 lg:py-24">
          <div className="container-x">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              
              {/* Sticky Sidebar Navigation */}
              <nav className="lg:col-span-4 sticky top-28 hidden lg:block bg-white border border-border/40 rounded-3xl p-6 shadow-[0_8px_32px_rgba(14,35,51,0.02)]">
                <p className="text-[12px] uppercase tracking-[0.18em] text-ink/50 font-semibold mb-4">
                  Table of Contents
                </p>
                <ul className="space-y-3.5">
                  {sections.map((sec) => (
                    <li key={sec.id}>
                      <a
                        href={`#${sec.id}`}
                        className="block text-[14.5px] text-ink/70 hover:text-forest transition-colors font-medium border-l-2 border-transparent hover:border-forest/40 pl-3 leading-snug"
                      >
                        {sec.title}
                      </a>
                    </li>
                  ))}
                </ul>

                <hr className="my-6 border-border/60" />

                <div className="rounded-2xl bg-cream/70 p-4 border border-border/40">
                  <div className="flex gap-2 text-forest text-[13px] font-bold uppercase tracking-wider items-center">
                    <Mail className="h-4 w-4" />
                    Support Email
                  </div>
                  <p className="mt-2 text-[12.5px] text-ink/65 leading-relaxed">
                    Write to us at hello@telemedcare.app to trigger your deletion request manually.
                  </p>
                </div>
              </nav>

              {/* Main Deletion Content */}
              <div className="lg:col-span-8 max-w-3xl">
                
                {/* Visual Highlights Box */}
                <div className="mb-12 rounded-3xl bg-forest text-cream p-7 lg:p-9 shadow-lg relative overflow-hidden">
                  <h2 className="font-display text-[22px] font-extrabold tracking-tight mb-3">
                    Fast Account Deletion Request
                  </h2>
                  <p className="text-[15px] text-cream/90 leading-relaxed mb-6">
                    If you want to close your account and remove your records, the fastest way is to send an email. We will verify your identity and delete everything immediately.
                  </p>
                  
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={mailtoUrl}
                      className="inline-flex items-center gap-2 rounded-full bg-clay hover:bg-clayHover text-cream px-7 py-3.5 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
                    >
                      <Mail className="h-5 w-5" />
                      Email hello@telemedcare.app
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>

                {/* Section Content */}
                <article className="prose prose-slate max-w-none">
                  
                  {/* Overview */}
                  <section id="overview" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      1. Deletion Overview
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      At TeleMedCare, we believe you should have complete control over your health and contact data. Under the <strong>Nigeria Data Protection Act (NDPA) 2023</strong>, you have the right to request the erasure of your personal information.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed">
                      This page explains exactly how to submit a deletion request, what data is permanently wiped from our databases, and what data we are required to keep under medical compliance laws.
                    </p>
                  </section>

                  {/* How to Request Deletion */}
                  <section id="how-to" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      2. How to Request Deletion
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      To delete your profile, clinical history, and messaging records, follow these instructions:
                    </p>
                    
                    <ol className="list-decimal pl-5 mb-6 text-[15.5px] text-ink/70 space-y-3">
                      <li>
                        Compose an email to <a href="mailto:hello@telemedcare.app" className="text-forest font-semibold hover:underline">hello@telemedcare.app</a>.
                      </li>
                      <li>
                        Set the subject line to <strong>"Account & Data Deletion Request"</strong>.
                      </li>
                      <li>
                        In the body of the email, provide your registered <strong>full name</strong> and the **WhatsApp phone number** associated with your TeleMedCare interactions.
                      </li>
                      <li>
                        Send the email from the email address registered with your patient portal account (this helps us verify that you are the true owner of the account).
                      </li>
                    </ol>

                    <div className="rounded-2xl border border-clay/30 bg-clay/5 p-5">
                      <p className="text-[14px] text-ink font-semibold flex items-center gap-1.5 text-clay">
                        Identity Verification
                      </p>
                      <p className="mt-1 text-[13.5px] text-ink/75 leading-relaxed">
                        To protect your medical privacy, we cannot delete records based on anonymous requests. We will call or SMS your registered WhatsApp contact number to confirm the deletion before making changes permanent.
                      </p>
                    </div>
                  </section>

                  {/* What Data Is Deleted */}
                  <section id="what-deleted" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      3. What Data Is Deleted
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      When your request is successfully verified and completed, the following data is permanently purged from our active systems:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      <li className="flex gap-2.5 items-start">
                        <CheckCircle2 className="h-4.5 w-4.5 text-forest shrink-0 mt-0.5" />
                        <span className="text-[14.5px] text-ink/85">User profile (name, gender, DOB)</span>
                      </li>
                      <li className="flex gap-2.5 items-start">
                        <CheckCircle2 className="h-4.5 w-4.5 text-forest shrink-0 mt-0.5" />
                        <span className="text-[14.5px] text-ink/85">Contact info (phone, email, address)</span>
                      </li>
                      <li className="flex gap-2.5 items-start">
                        <CheckCircle2 className="h-4.5 w-4.5 text-forest shrink-0 mt-0.5" />
                        <span className="text-[14.5px] text-ink/85">WhatsApp chat history & transcripts</span>
                      </li>
                      <li className="flex gap-2.5 items-start">
                        <CheckCircle2 className="h-4.5 w-4.5 text-forest shrink-0 mt-0.5" />
                        <span className="text-[14.5px] text-ink/85">Attached photos, audio, and documents</span>
                      </li>
                      <li className="flex gap-2.5 items-start">
                        <CheckCircle2 className="h-4.5 w-4.5 text-forest shrink-0 mt-0.5" />
                        <span className="text-[14.5px] text-ink/85">Portal credentials and active sessions</span>
                      </li>
                      <li className="flex gap-2.5 items-start">
                        <CheckCircle2 className="h-4.5 w-4.5 text-forest shrink-0 mt-0.5" />
                        <span className="text-[14.5px] text-ink/85">Past payment transaction association</span>
                      </li>
                    </ul>
                  </section>

                  {/* What Data Is Retained */}
                  <section id="what-retained" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      4. What Data Is Retained
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Under Nigerian medical council guidelines and healthcare retention regulations, medical records have legally mandated archiving periods.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      While we will delete your active profile, user account, and messaging history, <strong>core medical consultation records (such as clinical diagnostic notes, issued prescriptions, and lab test orders)</strong> will be securely archived in our encrypted EMR systems for the duration required by law.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed">
                      These archived clinical records are isolated and only accessible to authorize auditors or legal representatives in compliance with healthcare standard audits.
                    </p>
                  </section>

                  {/* Timeframe & Completion */}
                  <section id="timeframe" className="scroll-mt-24 mb-6">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      5. Timeframe & Completion
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Once we receive your email and verify your identity:
                    </p>
                    <ul className="list-disc pl-5 mb-6 text-[15px] text-ink/70 space-y-2">
                      <li>Your account portal access will be disabled immediately.</li>
                      <li>We will complete the database deletion process within <strong>7 business days</strong>.</li>
                      <li>You will receive a final confirmation email once the process is complete.</li>
                    </ul>

                    <div className="rounded-3xl border border-border/60 bg-white p-6 shadow-sm">
                      <p className="font-display text-[16px] font-bold text-ink mb-1">
                        Data Protection & Deletion Team
                      </p>
                      <p className="text-[14.5px] text-ink/70 mb-3">
                        TeleMedCare (Hivebrain LTD)
                      </p>
                      <a
                        href={mailtoUrl}
                        className="inline-flex items-center gap-2 text-forest hover:text-forestHover font-semibold text-[14.5px]"
                      >
                        <Mail className="h-4.5 w-4.5" />
                        hello@telemedcare.app
                      </a>
                    </div>
                  </section>

                </article>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
